import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Backdrop, CircularProgress, Snackbar, Autocomplete, TextField, Checkbox, FormControlLabel } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ImagePlus, Trash2, X } from "lucide-react";
import { getObtenerPromocion, postAddPromocion, postUpdatePromocion, getLocales, getLocalesFiltrarPorNombre } from "../../../api/ApiService";
import HeaderCustom from "../../../components/HeaderCustom";

export default function PromocionAddEditPage() {
    const { id } = useParams();
    const stateToken = useSelector((state) => state.reducerGlobal.token);

    const [loadingPage, setLoadingPage] = useState(Boolean(id));
    const [loadingGuardar, setLoadingGuardar] = useState(false);
    const [loadingLocales, setLoadingLocales] = useState(false);

    const [open, setOpen] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [mensaje, setMensaje] = useState("");

    const [formData, setFormData] = useState({
        titulo: "",
        resumen: "",
        descripcion: "",
        all_locales_label: "",
        imagenPreview: "",
        imagenFile: null,
        vigencia_promocion_desde: null,
        vigencia_promocion_hasta: null,
        vigencia_desde: null,
        vigencia_hasta: null,
        imagenes: [], // Array de imágenes secundarias (base64 o URLs)
    });

    const [locales, setLocales] = useState([]);
    const [localesSelected, setLocalesSelected] = useState([]);
    const [localesSearch, setLocalesSearch] = useState("");
    const [todosLosLocales, setTodosLosLocales] = useState(false);


    const [searchParams] = useSearchParams();

    const mode = searchParams.get('mode');


    const navigate = useNavigate();

    const autocompleteInputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f1f5f9',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
        },
    };



    const openAlert = (type, text) => {
        setSeverity(type);
        setMensaje(text);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const toBackendDateTime = (value) => {
        if (!value) return "";
        return dayjs(value).format("YYYY/MM/DD HH:mm:ss");
    };

    // Cargar detalle de promoción si está en modo edición
    useEffect(() => {
        if (!id || !stateToken?.access_token) return;

        setLoadingPage(true);

        getObtenerPromocion(id, stateToken.access_token)
            .then((response) => {
                const data = response || {};
                // console.log("data promocion a editar==>", data);

                // `imagenes` llega como array cuando la promocion tiene imagenes
                // secundarias, pero como el string "#" cuando no tiene ninguna
                // (accessor del backend). Normalizamos ambos casos antes de iterar:
                // sobre un string, .map() no existe y revienta el .then().
                const imagenesSecundarias = (Array.isArray(data?.imagenes) ? data.imagenes : []).map((url) => ({
                    preview: url,
                    original_url: url,
                    isNew: false,
                    file: null,
                }));

                setFormData((prev) => ({
                    ...prev,
                    titulo: data.titulo || "",
                    resumen: data.resumen || "",
                    descripcion: data.descripcion || "",
                    // "#" es el centinela del backend para "sin imagen": no es una URL
                    // valida, asi que se trata como vacio para no renderizar un img roto.
                    imagenPreview: data.imagen && data.imagen !== "#" ? data.imagen : "",
                    all_locales_label: data.all_locales_label || "",
                    imagenFile: null,
                    vigencia_promocion_desde: data.vigencia_promocion_desde ? dayjs(data.vigencia_promocion_desde) : null,
                    vigencia_promocion_hasta: data.vigencia_promocion_hasta ? dayjs(data.vigencia_promocion_hasta) : null,
                    vigencia_desde: data.vigencia_desde ? dayjs(data.vigencia_desde) : null,
                    vigencia_hasta: data.vigencia_hasta ? dayjs(data.vigencia_hasta) : null,
                    imagenes: imagenesSecundarias,
                }));

                if (data?.all_locales) {
                    setTodosLosLocales(true);
                    setLocalesSelected(data?.locales || []);
                } else {
                    setTodosLosLocales(false);
                    setLocalesSelected(data?.locales || []);
                }
            })
            .catch(() => {
                openAlert("error", "Error al cargar los datos de la promoción.");
            })
            .finally(() => {
                setLoadingPage(false);
            });
    }, [id, stateToken?.access_token]);

    // Cargar locales iniciales (tambien en edicion: el listado de opciones es
    // necesario para poder mostrar y modificar los locales ya seleccionados)
    useEffect(() => {
        if (stateToken?.access_token) {
            setLoadingLocales(true);
            getLocales(stateToken.access_token)
                .then((response) => {
                    setLocales(response?.data || []);
                })
                .catch(() => {
                    openAlert("error", "Error al cargar los locales.");
                })
                .finally(() => {
                    setLoadingLocales(false);
                });
        }
    }, [stateToken?.access_token]);

    // Filtrar locales por búsqueda
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (localesSearch.length > 2 && stateToken?.access_token) {
                setLoadingLocales(true);
                getLocalesFiltrarPorNombre(localesSearch, stateToken.access_token)
                    .then((response) => {
                        setLocales(response?.data || []);
                    })
                    .catch(() => {
                        setLocales([]);
                    })
                    .finally(() => {
                        setLoadingLocales(false);
                    });
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [localesSearch, stateToken?.access_token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAgregarImagenPrincipal = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result;
            if (!result) return;

            const img = new Image();
            img.onload = () => {
                if (img.width === 720 && img.height === 720) {
                    setFormData((prev) => ({
                        ...prev,
                        imagenPreview: result,
                        imagenFile: file,
                    }));
                } else {
                    openAlert("error", "La imagen debe tener dimensiones 720x720 px.");
                }
            };
            img.src = result;
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImagenPrincipal = () => {
        setFormData((prev) => ({
            ...prev,
            imagenPreview: "",
            imagenFile: null,
        }));

        const fileInput = document.getElementById("imagen_principal");
        if (fileInput) fileInput.value = "";
    };

    const handleAgregarImagenesSecundarias = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const filePromises = files.map(
            (file) =>
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        resolve({
                            file,
                            preview: e.target.result,
                            isNew: true,
                        });
                    };
                    reader.readAsDataURL(file);
                })
        );

        Promise.all(filePromises).then((newImages) => {
            setFormData((prev) => ({
                ...prev,
                imagenes: [...prev.imagenes, ...newImages],
            }));
        });
    };

    const handleRemoveImagenSecundaria = (index) => {
        setFormData((prev) => ({
            ...prev,
            imagenes: prev.imagenes.filter((_, i) => i !== index),
        }));
    };

    const validateForm = () => {
        const { titulo, resumen, descripcion, all_locales_label, imagenPreview, vigencia_promocion_desde, vigencia_promocion_hasta, vigencia_desde, vigencia_hasta } = formData;

        if (!titulo.trim()) return "El título es obligatorio.";
        if (!imagenPreview) return "La imagen principal es obligatoria.";
        if (!resumen.trim()) return "El resumen es obligatorio.";
        if (!descripcion.trim()) return "La descripción es obligatoria.";
        if (todosLosLocales === true && !all_locales_label.trim()) return "La descripción a mostrar es obligatoria cuando se selecciona 'Todos los locales'.";

        if (localesSelected.length === 0 && !todosLosLocales) {
            return "Debes seleccionar al menos un local.";
        }

        if (!vigencia_promocion_desde || !vigencia_promocion_hasta || !vigencia_desde || !vigencia_hasta) {
            return "Todas las fechas son obligatorias.";
        }

        if (!dayjs(vigencia_promocion_desde).isValid() || !dayjs(vigencia_promocion_hasta).isValid()) {
            return "Las fechas de vigencia de promoción no son válidas.";
        }

        if (dayjs(vigencia_promocion_desde).isAfter(dayjs(vigencia_promocion_hasta))) {
            return "La fecha de fin de promoción no puede ser menor a la inicial.";
        }

        if (!dayjs(vigencia_desde).isValid() || !dayjs(vigencia_hasta).isValid()) {
            return "Las fechas de vigencia de publicación no son válidas.";
        }

        if (dayjs(vigencia_desde).isAfter(dayjs(vigencia_hasta))) {
            return "La fecha de fin de publicación no puede ser menor a la inicial.";
        }

        return "";
    };





    const buildPayload = () => {
        const payload = new FormData();

        payload.append("titulo", formData.titulo);
        payload.append("resumen", formData.resumen);
        payload.append("descripcion", formData.descripcion);
        payload.append("all_locales_label", formData.all_locales_label || "");

        payload.append("vigencia_promocion_desde", toBackendDateTime(formData.vigencia_promocion_desde));
        payload.append("vigencia_promocion_hasta", toBackendDateTime(formData.vigencia_promocion_hasta));

        payload.append("vigencia_desde", toBackendDateTime(formData.vigencia_desde));
        payload.append("vigencia_hasta", toBackendDateTime(formData.vigencia_hasta));

        if (formData.imagenFile) {
            payload.append("imagen", formData.imagenFile);
        }

        const localesIds = localesSelected
            .map((l) => Number(l?.id))
            .filter((idLocal) => Number.isInteger(idLocal));
        // const localesLabel = todosLosLocales
        //     ? "Todos"
        //     : localesSelected.map((l) => l.nombre_fantasia || l.nombre).join(",");

        localesIds.forEach((idLocal) => {
            payload.append("locales[]", String(idLocal));
        });
        // payload.append("all_locales_label", String(localesLabel || ""));
        payload.append("all_locales", todosLosLocales ? 1 : 0);

        // Agregar imágenes secundarias como array: imagenes[]
        const imagenesNuevas = formData.imagenes
            .filter((img) => img.isNew && img.file)
            .map((img) => img.file);

        imagenesNuevas.forEach((file) => {
            payload.append("imagenes[]", file);
        });

        //poner en consola lo que se va a enviar al backend, para verificar que el formato de las fechas sea correcto y que la imagen se haya agregado correctamente
        // console.log("Payload a enviar:");
        // for (let pair of payload.entries()) {
        //     console.log(`${pair[0]}: ${pair[1]}`);
        // }

        return payload;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            openAlert("warning", validationError);
            return;
        }

        if (!stateToken?.access_token) {
            openAlert("error", "No se encontró token de autenticación.");
            return;
        }

        const payload = buildPayload();
        setLoadingGuardar(true);

        const request = id ? postUpdatePromocion(id, payload, stateToken.access_token) : postAddPromocion(payload, stateToken.access_token);

        request
            .then(() => {
                openAlert("success", id ? "Promoción actualizada con éxito." : "Promoción creada con éxito.");

                setTimeout(() => {
                    navigate('/app/admin/promociones');
                }, 1500);
            })
            .catch(() => {
                openAlert("error", id ? "Error al actualizar la promoción." : "Error al crear la promoción.");
            })
            .finally(() => {
                setLoadingGuardar(false);
            });
    };

    const handleApiAgregarImagenes = () => {
        const validationError = validateForm();
        if (validationError) {
            openAlert("warning", validationError);
            return;
        };

        if (!stateToken?.access_token) {
            openAlert("error", "No se encontró token de autenticación.");
            return;
        };

        const payload = buildPayload();
        setLoadingGuardar(true);

        const request = postAddPromocion(payload, stateToken.access_token);

        request
            .then((resp) => {
                openAlert("success", "Promoción creada con éxito.");

                setTimeout(() => {
                    navigate(`/app/admin/promocion/agregar-imagen/${resp?.data.id}?mode=agregar`);
                }, 1500);


            })
            .catch(() => {
                openAlert("error", "Error al crear la promoción.");
            })
            .finally(() => {
                setLoadingGuardar(false);
            });
    };







    const handleCancelar = () => {
        window.history.back();
    };

    return (
        <HeaderCustom>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-6">
                            <h1 className="text-2xl font-bold text-gray-800 mb-1">{id ? "Editar Promoción" : "Nueva Promoción"}</h1>
                            <p className="text-sm text-gray-500">Completa todos los campos para crear o actualizar una promoción.</p>
                        </div>

                        {loadingPage ? (
                            <div className="bg-white rounded-xl shadow-md p-8">
                                <div className="space-y-4">
                                    <div className="h-10 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-10 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-24 bg-gray-200 rounded animate-pulse" />
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                                <form onSubmit={handleSubmit}>
                                    {/* Datos principales */}
                                    <div className="p-6 ">

                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Información de Promoción</h2>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> */}
                                            <div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                                                    <input
                                                        type="text"
                                                        name="titulo"
                                                        value={formData.titulo}
                                                        onChange={handleInputChange}
                                                        placeholder="Ej: Descuento 50%"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>







                                                <div className="mt-4 flex flex-col gap-6 md:flex-row">

                                                    {todosLosLocales && (
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Descripcion a mostrar</label>
                                                            <input
                                                                type="text"
                                                                name="all_locales_label"
                                                                value={formData.all_locales_label}
                                                                onChange={handleInputChange}
                                                                placeholder="Ej: Disponible en todos los locales"
                                                                required
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                            />
                                                        </div>
                                                    )}


                                                    {!todosLosLocales && (
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Locales</label>

                                                            <Autocomplete
                                                                multiple
                                                                loading={loadingLocales}
                                                                options={locales}
                                                                getOptionLabel={(option) => option?.nombre_fantasia || option?.nombre || ""}
                                                                value={localesSelected}
                                                                onChange={(e, newValue) => setLocalesSelected(newValue)}
                                                                onInputChange={(e, newInputValue) => setLocalesSearch(newInputValue)}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                filterSelectedOptions
                                                                renderInput={(params) => <TextField
                                                                    sx={autocompleteInputSx}
                                                                    {...params}
                                                                    label="Seleccionar locales"
                                                                    size="small"
                                                                    placeholder="Busca locales..."
                                                                />
                                                                }
                                                            />
                                                        </div>
                                                    )}


                                                    <div className="">
                                                        <FormControlLabel
                                                            control={<Checkbox checked={todosLosLocales} onChange={(e) => setTodosLosLocales(e.target.checked)} />}
                                                            label="Todos los locales"
                                                        />
                                                    </div>

                                                </div>

                                            </div>



                                            {/* Imagen principal */}
                                            <div className="">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Imagen Principal (720x720)
                                                </label>

                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                                                    <input id="imagen_principal" type="file" accept="image/*" className="hidden" onChange={handleAgregarImagenPrincipal} />

                                                    {!formData.imagenPreview ? (
                                                        <label htmlFor="imagen_principal" className="cursor-pointer flex flex-col items-center text-center">
                                                            <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                                                            <p className="text-sm text-gray-600">Seleccionar imagen</p>
                                                            <p className="text-xs text-gray-500 mt-1">PNG o JPG con tamaño exacto 720x720</p>
                                                        </label>
                                                    ) : (
                                                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                                            <img src={formData.imagenPreview} alt="Vista previa" className="w-36 h-36 rounded-md object-cover" />
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveImagenPrincipal}
                                                                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Cambiar imagen
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>






                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Resumen</label>
                                                <textarea
                                                    name="resumen"
                                                    value={formData.resumen}
                                                    rows={10}
                                                    onChange={handleInputChange}
                                                    placeholder="Resumen corto de la promoción"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                                                <textarea
                                                    name="descripcion"
                                                    value={formData.descripcion}
                                                    onChange={handleInputChange}
                                                    rows={10}
                                                    placeholder="Descripción detallada de la promoción"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>








                                    {/* Imagen principal */}
                                    {/* <div className="p-6 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Imagen Principal (720x720)</h2>

                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                                            <input id="imagen_principal" type="file" accept="image/*" className="hidden" onChange={handleAgregarImagenPrincipal} />

                                            {!formData.imagenPreview ? (
                                                <label htmlFor="imagen_principal" className="cursor-pointer flex flex-col items-center text-center">
                                                    <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                                                    <p className="text-sm text-gray-600">Seleccionar imagen</p>
                                                    <p className="text-xs text-gray-500 mt-1">PNG o JPG con tamaño exacto 720x720</p>
                                                </label>
                                            ) : (
                                                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                                    <img src={formData.imagenPreview} alt="Vista previa" className="w-36 h-36 rounded-md object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveImagenPrincipal}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Cambiar imagen
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div> */}

                                    {/* Fechas de vigencia */}
                                    <div className="p-6">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Fecha del evento</h2>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_promocion_desde}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_promocion_desde: newValue }))}
                                                    ampm={false}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                    format="DD/MM/YYYY HH:mm"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_promocion_hasta}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_promocion_hasta: newValue }))}
                                                    ampm={false}
                                                    minDateTime={formData.vigencia_promocion_desde || undefined}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                    format="DD/MM/YYYY HH:mm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fechas de publicación */}
                                    <div className="p-6">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Vigencia de Publicación</h2>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_desde}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_desde: newValue }))}
                                                    ampm={false}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                    format="DD/MM/YYYY HH:mm"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_hasta}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_hasta: newValue }))}
                                                    ampm={false}
                                                    minDateTime={formData.vigencia_desde || undefined}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                    format="DD/MM/YYYY HH:mm"
                                                />
                                            </div>
                                        </div>
                                    </div>



                                    {/* Imágenes secundarias */}
                                    {/* <div className="p-6 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Imágenes Secundarias (Galería)</h2>

                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors mb-4">
                                            <input id="imagenes_secundarias" type="file" accept="image/*" multiple className="hidden" onChange={handleAgregarImagenesSecundarias} />

                                            <label htmlFor="imagenes_secundarias" className="cursor-pointer flex flex-col items-center text-center">
                                                <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                                                <p className="text-sm text-gray-600">Agregar más imágenes</p>
                                                <p className="text-xs text-gray-500 mt-1">Puedes subir múltiples imágenes</p>
                                            </label>
                                        </div>

                                        {formData.imagenes.length > 0 && (
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                                {formData.imagenes.map((img, index) => {
                                                    const imageSource = img.preview || img.original_url;
                                                    return (
                                                        <div key={index} className="relative">
                                                            <img src={imageSource} alt={`Galería ${index}`} className="w-full h-32 object-cover rounded-md" />

                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveImagenSecundaria(index)}
                                                                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md"
                                                                title="Eliminar imagen"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div> */}

                                    {/* Botones */}
                                    <div className="flex justify-start gap-3 p-6 border-t border-gray-200">
                                        <button type="submit" disabled={loadingGuardar} className="px-6 py-2 bg-[#104063] hover:bg-[#0d3553] disabled:opacity-70 text-white rounded-lg font-medium transition-colors">
                                            {loadingGuardar ? "Guardando..." : id ? "Guardar Cambios" : "Guardar"}
                                        </button>

                                        {mode !== "editar" && (
                                            <button type="button" disabled={loadingGuardar} className="px-6 py-2 bg-[#104063] hover:bg-[#0d3553] disabled:opacity-70 text-white rounded-lg font-medium transition-colors" onClick={handleApiAgregarImagenes}>
                                                {/* {loadingGuardar ? "Guardando..." : id ? "Guardar Cambios" : "Guardar"} */}
                                                Guardar y agregar imágenes
                                            </button>
                                        )}


                                        <div className="border-l border-gray-300" />
                                        <button type="button" onClick={handleCancelar} className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors">
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>

                    <Backdrop open={loadingGuardar} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
                        <CircularProgress color="inherit" />
                    </Backdrop>

                    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={open} autoHideDuration={2200} onClose={handleClose}>
                        <Alert onClose={handleClose} severity={severity} elevation={6} variant="filled">
                            {mensaje}
                        </Alert>
                    </Snackbar>
                </div>
            </LocalizationProvider>
        </HeaderCustom>
    );
}
